import React, { useState } from 'react';
import {
    Card,
    Button,
    Space,
    Typography,
    Statistic,
    Row,
    Col,
    Alert,
    Spin,
    message,
    Badge,
} from 'antd';
import {
    ReloadOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetJobLocksQuery,
    useForceUnlockJobMutation,
    useRunJobManuallyMutation,
} from '../../../services/nestjs-base-api';
import { ActiveLocksTable } from './ActiveLocksTable';
import { RegisteredJobsTable } from './RegisteredJobsTable';
import { JobHistoryModal } from './JobHistoryModal';

const { Text } = Typography;

export const JobLockingTab: React.FC = () => {
    const { user } = useAuth();
    const [selectedJob, setSelectedJob] = useState<string | null>(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    const {
        data: jobLocksData,
        isLoading,
        error,
        refetch,
    } = useGetJobLocksQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [forceUnlock, { isLoading: isUnlocking }] = useForceUnlockJobMutation();
    const [runJob, { isLoading: isRunning }] = useRunJobManuallyMutation();

    const handleForceUnlock = async (jobName: string) => {
        try {
            const result = await forceUnlock({
                jwtToken: user?.jwtToken || '',
                jobName,
            }).unwrap();

            message.success(result.message);
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to unlock job');
        }
    };

    const handleRunJob = async (jobName: string) => {
        try {
            const result = await runJob({
                jwtToken: user?.jwtToken || '',
                jobName,
            }).unwrap();

            if (result.success) {
                message.success(result.message);
            } else {
                message.warning(result.message);
            }
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to run job');
        }
    };

    const handleViewHistory = (jobName: string) => {
        setSelectedJob(jobName);
        setIsHistoryModalOpen(true);
    };

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error loading job locking data"
                description="Unable to fetch job locking information. Please try again later."
                type="error"
                showIcon
            />
        );
    }

    const { registeredJobs = [], activeLocks = [] } = jobLocksData || {};
    const totalJobs = registeredJobs.length;
    const lockedJobs = activeLocks.length;
    const expiredLocks = activeLocks.filter(lock => lock.isExpired).length;

    return (
        <div>
            {/* Header with action buttons */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                }}
            >
                <div>
                    <Text type="secondary">
                        Monitor and manage distributed job scheduling locks
                    </Text>
                </div>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => refetch()}
                        loading={isLoading}
                    >
                        Refresh
                    </Button>
                </Space>
            </div>

            {/* Statistics cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Total Jobs"
                            value={totalJobs}
                            prefix={<SettingOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Currently Locked"
                            value={lockedJobs}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: lockedJobs > 0 ? '#faad14' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Expired Locks"
                            value={expiredLocks}
                            prefix={<ExclamationCircleOutlined />}
                            valueStyle={{ color: expiredLocks > 0 ? '#ff4d4f' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Available Jobs"
                            value={totalJobs - lockedJobs}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Active Locks Section */}
            <Card
                title="Active Locks"
                style={{ marginBottom: 24 }}
                extra={
                    activeLocks.length > 0 && (
                        <Badge count={activeLocks.length} showZero />
                    )
                }
            >
                <ActiveLocksTable
                    activeLocks={activeLocks}
                    onForceUnlock={handleForceUnlock}
                    isUnlocking={isUnlocking}
                />
            </Card>

            {/* Registered Jobs Section */}
            <Card title="Registered Jobs">
                <RegisteredJobsTable
                    registeredJobs={registeredJobs}
                    onRunJob={handleRunJob}
                    onViewHistory={handleViewHistory}
                    isRunning={isRunning}
                />
            </Card>

            {/* Job History Modal */}
            <JobHistoryModal
                jobName={selectedJob}
                isOpen={isHistoryModalOpen}
                onClose={() => {
                    setIsHistoryModalOpen(false);
                    setSelectedJob(null);
                }}
            />
        </div>
    );
};